import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, computed, effect, inject, PLATFORM_ID, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  EstabelecimentoDataService,
  ServicoService,
  TipoServicoDataService,
  TipoServicoService,
  BardoContextDataService,
  NichoTermosService,
} from '@nomoh-monorepo/data-access-bardo-administrador';
import { BottomSheetComponent, DeviceService } from '@nomoh-monorepo/shared-common-ui';
import type { IServico, ITipoServico } from '@nomoh-monorepo/shared-types';
import { I18nService } from '@nomoh-monorepo/data-access-shared-i18n';
import { formatarDuracao } from '@nomoh-monorepo/shared-util-tools';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TipoServicoFormModalComponent } from './tipo-servico-form-modal/tipo-servico-form-modal.component';
import { ServicoFormModalComponent } from './servico-form-modal/servico-form-modal.component';

interface TipoServicoExpandido extends ITipoServico {
  servicos?: IServico[];
}

@Component({
  selector: 'lib-tipo-servico',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TooltipModule,
    ConfirmDialogModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    FormsModule,
    TipoServicoFormModalComponent,
    ServicoFormModalComponent,
    BottomSheetComponent,
    TranslateModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './tipo-servico.component.html',
  styleUrl: './tipo-servico.component.scss',
})
export class TipoServicoComponent {
  // Injeção de dependências
  private platformId = inject(PLATFORM_ID);
  private tipoServicoService = inject(TipoServicoService);
  private tipoServicoDataService = inject(TipoServicoDataService);
  private servicoService = inject(ServicoService);
  private estabelecimentoDataService = inject(EstabelecimentoDataService);
  private nichoTermosService = inject(NichoTermosService);
  private confirmationService = inject(ConfirmationService);
  private contextoService = inject(BardoContextDataService);
  private translateService = inject(TranslateService);
  private i18n = inject(I18nService);
  deviceService = inject(DeviceService);

  $termos = this.nichoTermosService.$termos;
  $nicho = this.nichoTermosService.$nicho;


  // Confirmations Mobile
  modalExclusaoTipoAberto = signal(false);
  tipoParaExcluir = signal<TipoServicoExpandido | null>(null);
  mensagemExclusaoTipo = signal('');
  excluindoTipo = signal(false);

  modalExclusaoServicoAberto = signal(false);
  servicoParaExcluir = signal<IServico | null>(null);
  excluindoServico = signal(false);

  // Signals - Estado
  isLoading = signal<boolean>(false);

  modalCategoriaVisivel = signal<boolean>(false);
  modalServicoVisivel = signal<boolean>(false);

  // Mobile detection
  private _isMobile = signal<boolean>(false);
  $isMobile = this._isMobile.asReadonly();

  servicoEdicao = signal<IServico | null>(null);
  tipoServicoSelecionado = signal<ITipoServico | null>(null);

  // Categoria em foco: quando preenchido, exibe a tela dedicada daquela categoria
  private categoriaFocoId = signal<string | null>(null);

  // Paginação de serviços na tela de detalhe da categoria
  servicosExpandidos = signal<Map<string, number>>(new Map()); // tipoId → qtd visível

  // Filtro de busca local
  pesquisa = signal('');
  pesquisaServico = signal('');

  // Computed
  estabelecimentoId = computed(() => {
    return this.estabelecimentoDataService.$estabelecimentoAtual()?.id || '';
  });

  tiposServico = computed(() => {
    return (this.tipoServicoDataService.$tiposServico() ||
      []) as TipoServicoExpandido[];
  });

  tiposServicoFiltrados = computed(() => {
    const todos = this.tiposServico() || [];
    const termo = this.pesquisa().trim().toLowerCase();
    if (!termo) return todos;

    return todos.filter((tipo) => {
      const nomeTipo = (tipo.nome || '').toLowerCase();
      const matchTipo = nomeTipo.includes(termo);
      const matchServico = (tipo.servicos || []).some((s) =>
        (s.nome || '').toLowerCase().includes(termo)
      );
      return matchTipo || matchServico;
    });
  });

  categoriaFoco = computed<TipoServicoExpandido | null>(() => {
    const focoId = this.categoriaFocoId();
    if (!focoId) return null;
    return this.tiposServico().find((t) => t.id === focoId) ?? null;
  });

  loading = computed(() => {
    return this.tipoServicoDataService.$loading() || this.isLoading();
  });

  possuiPermissao(permissao: string) {
    return this.contextoService?.possuiPermissao(permissao) ?? false;
  }

  private ultimoEstabelecimentoCarregado = '';

  constructor() {
    this.detectMobile();

    effect(() => {
      const estabelecimentoId = this.estabelecimentoId();
      if (!estabelecimentoId) {
        return;
      }

      if (this.ultimoEstabelecimentoCarregado === estabelecimentoId) {
        return;
      }

      void this.carregarTiposServico(estabelecimentoId);
    });
  }

  private detectMobile() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const checkMobile = () => {
      const userAgent = navigator.userAgent || '';
      const isMobileUA =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          userAgent
        );
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth <= 768;

      this._isMobile.set(isMobileUA || (hasTouch && isSmallScreen));
    };

    checkMobile();

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', checkMobile);
    }
  }

  // ==================== CARREGAMENTO ====================

  async carregarTiposServico(estabelecimentoId?: string) {
    try {
      this.isLoading.set(true);
      const alvoEstabelecimentoId =
        estabelecimentoId || this.estabelecimentoId();

      if (!alvoEstabelecimentoId) {
        return;
      }

      // Buscar tipos
      const tipos = await this.tipoServicoService.findAll({
        estabelecimentoId: alvoEstabelecimentoId,
      });

      // Buscar todos os serviços
      const servicos = await this.servicoService.findAll({
        estabelecimentoId: alvoEstabelecimentoId,
      });

      // Aninhar serviços nos tipos
      const tiposComServicos = tipos.map((tipo) => ({
        ...tipo,
        servicos: servicos.filter((s) => s.tipoServicoId === tipo.id),
      }));

      this.tipoServicoDataService.setTiposServico(
        tiposComServicos as ITipoServico[]
      );

      this.ultimoEstabelecimentoCarregado = alvoEstabelecimentoId;
    } catch {
      // interceptor handles error notification
    } finally {
      this.isLoading.set(false);
    }
  }

  // ==================== TELA DE DETALHE DA CATEGORIA ====================

  abrirCategoria(tipoId: string) {
    this.categoriaFocoId.set(tipoId);
  }

  voltarParaListagem() {
    this.categoriaFocoId.set(null);
  }

  // ==================== PAGINAÇÃO DE SERVIÇOS ====================

  getServicosFiltrados(tipo: TipoServicoExpandido): IServico[] {
    let servicos = tipo.servicos || [];
    const termo = this.pesquisaServico().trim().toLowerCase();
    if (termo) {
      servicos = servicos.filter((s) => (s.nome || '').toLowerCase().includes(termo));
    }
    return servicos;
  }

  getServicosPaginados(tipo: TipoServicoExpandido): IServico[] {
    const servicos = this.getServicosFiltrados(tipo);
    const qtdVisivel = this.servicosExpandidos().get(tipo.id) || 5;
    return servicos.slice(0, qtdVisivel);
  }

  temMaisServicos(tipo: TipoServicoExpandido): boolean {
    const servicos = this.getServicosFiltrados(tipo);
    const qtdVisivel = this.servicosExpandidos().get(tipo.id) || 5;
    return servicos.length > qtdVisivel;
  }

  expandirServicos(tipoId: string) {
    const expandidos = new Map(this.servicosExpandidos());
    const atual = expandidos.get(tipoId) || 5;
    expandidos.set(tipoId, atual + 10);
    this.servicosExpandidos.set(expandidos);
  }

  recolherServicos(tipoId: string) {
    const expandidos = new Map(this.servicosExpandidos());
    expandidos.set(tipoId, 5);
    this.servicosExpandidos.set(expandidos);
  }

  // ==================== CRUD TIPO DE SERVIÇO ====================

  abrirModalNovoTipo() {
    this.tipoServicoSelecionado.set(null);
    this.modalCategoriaVisivel.set(true);
  }

  abrirModalEditarTipo(tipo: ITipoServico) {
    this.tipoServicoSelecionado.set(tipo);
    this.modalCategoriaVisivel.set(true);
  }

  excluirTipoServico(tipo: TipoServicoExpandido) {
    const qtdServicos = tipo.servicos?.length || 0;
    const termo =
      qtdServicos === 1
        ? this.$nicho().servico.singular.toLowerCase()
        : this.$nicho().servico.plural.toLowerCase();

    const mensagem =
      qtdServicos > 0
        ? this.translateService.instant(
            'BARDO_SERVICOS.CONFIRMAR_EXCLUSAO_CATEGORIA_COM_SERVICOS',
            { qtd: qtdServicos, termo }
          )
        : this.translateService.instant(
            'BARDO_SERVICOS.CONFIRMAR_EXCLUSAO_CATEGORIA'
          );

    if (this.deviceService.$isMobile()) {
      this.tipoParaExcluir.set(tipo);
      this.mensagemExclusaoTipo.set(mensagem);
      this.modalExclusaoTipoAberto.set(true);
    } else {
      this.tipoParaExcluir.set(tipo);
      this.confirmationService.confirm({
        message: mensagem,
        header: this.translateService.instant('GLOBAL.CONFIRMAR_EXCLUSAO'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translateService.instant('GLOBAL.SIM_EXCLUIR'),
        rejectLabel: this.translateService.instant('GLOBAL.CANCELAR'),
        acceptButtonStyleClass: 'p-button-danger',
        accept: () => this.confirmarExclusaoTipoConfirmada(),
      });
    }
  }

  async confirmarExclusaoTipoConfirmada(): Promise<void> {
    const tipo = this.tipoParaExcluir();
    if (!tipo) return;
    this.excluindoTipo.set(true);
    try {
      this.isLoading.set(true);
      await this.tipoServicoService.deletar(tipo.id);
      if (this.categoriaFocoId() === tipo.id) {
        this.voltarParaListagem();
      }
      this.modalExclusaoTipoAberto.set(false);
      this.tipoParaExcluir.set(null);
      await this.carregarTiposServico();
    } catch (err) {
      console.error('Erro ao excluir tipo de serviço', err);
    } finally {
      this.excluindoTipo.set(false);
      this.isLoading.set(false);
    }
  }

  // ==================== CRUD SERVIÇO ====================

  abrirModalNovoServico(tipoId: string) {
    const tipo =
      (this.tiposServico().find((t) => t.id === tipoId) as ITipoServico) ??
      null;
    this.tipoServicoSelecionado.set(tipo);
    this.servicoEdicao.set(null);
    this.modalServicoVisivel.set(true);
  }

  abrirModalEditarServico(servico: IServico) {
    this.servicoEdicao.set(servico);
    const tipo =
      (this.tiposServico().find(
        (t) => t.id === servico.tipoServicoId
      ) as ITipoServico) ?? null;
    this.tipoServicoSelecionado.set(tipo);
    this.modalServicoVisivel.set(true);
  }

  excluirServico(servico: IServico) {
    const termo = this.$nicho().servico.singular.toLowerCase();
    const mensagem = this.translateService.instant(
      'BARDO_SERVICOS.CONFIRMAR_EXCLUSAO_SERVICO',
      { termo }
    );

    if (this.deviceService.$isMobile()) {
      this.servicoParaExcluir.set(servico);
      this.modalExclusaoServicoAberto.set(true);
    } else {
      this.servicoParaExcluir.set(servico);
      this.confirmationService.confirm({
        message: mensagem,
        header: this.translateService.instant('GLOBAL.CONFIRMAR_EXCLUSAO'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translateService.instant('GLOBAL.SIM_EXCLUIR'),
        rejectLabel: this.translateService.instant('GLOBAL.CANCELAR'),
        acceptButtonStyleClass: 'p-button-danger',
        accept: () => this.confirmarExclusaoServicoConfirmada(),
      });
    }
  }

  async confirmarExclusaoServicoConfirmada(): Promise<void> {
    const servico = this.servicoParaExcluir();
    if (!servico) return;
    this.excluindoServico.set(true);
    try {
      this.isLoading.set(true);
      await this.servicoService.deletar(servico.id);
      this.modalExclusaoServicoAberto.set(false);
      this.servicoParaExcluir.set(null);
      await this.carregarTiposServico();
    } catch (err) {
      console.error('Erro ao excluir serviço', err);
    } finally {
      this.excluindoServico.set(false);
      this.isLoading.set(false);
    }
  }

  // ==================== CALLBACKS DOS MODAIS ====================

  async onModalTipoSalvo(novoId: string): Promise<void> {
    await this.carregarTiposServico();
    if (novoId) {
      this.abrirCategoria(novoId);
    }
  }

  async onModalServicoSalvo(): Promise<void> {
    await this.carregarTiposServico();
  }

  // ==================== FORMATAÇÃO ====================

  formatarPreco(valor?: number | null): string {
    if (valor === null || valor === undefined || valor === 0) {
      return this.translateService.instant('BARDO_SERVICOS.GRATIS') || 'Grátis';
    }
    return this.i18n.formatarMoeda(valor);
  }

  formatarDuracaoExibicao(valor?: number | string | null): string {
    const minutos = this.obterDuracaoEmMinutos(valor);

    if (minutos <= 0) {
      return '0min';
    }

    return formatarDuracao(minutos);
  }

  // ==================== GETTERS AUXILIARES ====================

  getContadorServicos(tipo: TipoServicoExpandido): string {
    const qtd = tipo.servicos?.length || 0;
    const termo =
      qtd === 1
        ? this.$nicho().servico.singular.toLowerCase()
        : this.$nicho().servico.plural.toLowerCase();
    return `${qtd} ${termo}`;
  }

  private obterDuracaoEmMinutos(valor?: number | string | null): number {
    if (typeof valor === 'number' && Number.isFinite(valor)) {
      return valor;
    }

    if (typeof valor === 'string') {
      const trimmed = valor.trim();
      if (!trimmed) {
        return 0;
      }

      if (trimmed.includes(':')) {
        const [h = '0', m = '0', s = '0'] = trimmed.split(':');
        const horas = Number(h);
        const minutos = Number(m);
        if (Number.isFinite(horas) && Number.isFinite(minutos)) {
          return horas * 60 + minutos + (Number(s) || 0) / 60;
        }
      }

      const numero = Number(trimmed);
      if (Number.isFinite(numero)) {
        return numero;
      }
    }

    return 0;
  }
}
